// HelpArticleService.js
//
// Help articles live under `_help/global/articles/{articleId}`.
// Older type-owned docs under `_nodetypes/{typeKey}/articles` are still
// readable and migrate into the library on save.

import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { collections, db } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';
import {
  HELP_APP_TYPE_KEY,
  HELP_IMAGE_MAX_BYTES,
  compareHelpArticles,
  getHelpArticleTypeKeys,
  getNextHelpArticlePosition,
  normalizeHelpString,
  parseHelpArticleTags,
  parseHelpArticleTypeKeys,
  resolveHelpArticleTypeKey,
} from 'utilities/HelpArticles';
import { createRichTextValue, extractRichTextPlainText, normalizeRichTextValue } from 'utilities/RichText';

const HELP_COLLECTION = collections.help || '_help';
const HELP_APP_DOC = collections.helpAppDoc || 'global';
const ARTICLES_SUBCOLLECTION = collections.helpArticles || 'articles';
const LIBRARY_PREFIX = `${HELP_COLLECTION}/${HELP_APP_DOC}/${ARTICLES_SUBCOLLECTION}/`;

/**
 * Returns the shared help article collection.
 *
 * @returns {object}
 */
function helpLibraryCollectionRef() {
  return collection(db, HELP_COLLECTION, HELP_APP_DOC, ARTICLES_SUBCOLLECTION);
}

/**
 * Returns a library article document ref.
 *
 * @param {string} articleId
 * @returns {object}
 */
function helpLibraryDocRef(articleId) {
  return doc(helpLibraryCollectionRef(), normalizeHelpString(articleId));
}

/**
 * Returns whether a snapshot path is already in the shared library.
 *
 * @param {string} path
 * @returns {boolean}
 */
function isLibraryPath(path) {
  return normalizeHelpString(path).startsWith(LIBRARY_PREFIX);
}

/**
 * Builds a document ref from a stored Firestore path.
 *
 * @param {string} path
 * @returns {object|null}
 */
function docRefFromPath(path) {
  const segments = normalizeHelpString(path).split('/').filter(Boolean);
  if (segments.length < 2) return null;
  return doc(db, ...segments);
}

/**
 * Storage folder for screenshots. Uses the first type, or APP when unscoped.
 *
 * @param {string[]} typeKeys
 * @returns {string}
 */
function storageScopeForTypeKeys(typeKeys) {
  return resolveHelpArticleTypeKey(Array.isArray(typeKeys) ? typeKeys[0] : '');
}

/**
 * Normalizes a stored or draft help article.
 *
 * @param {object} data
 * @param {string} [docId]
 * @param {object} [options]
 * @returns {object}
 */
export function normalizeHelpArticle(data = {}, docId = '', options = {}) {
  const key = normalizeHelpString(data?.key) || normalizeHelpString(docId);
  const content = normalizeRichTextValue(data?.content);
  const contentText = normalizeHelpString(data?.contentText)
    || extractRichTextPlainText(content);
  const typeKeys = parseHelpArticleTypeKeys(data?.typeKeys, data?.typeKey);

  return {
    id: key,
    key,
    typeKeys,
    typeKey: typeKeys[0] || HELP_APP_TYPE_KEY,
    title: normalizeHelpString(data?.title),
    tags: parseHelpArticleTags(data?.tags),
    content,
    contentText,
    published: data?.published === true,
    pinned: data?.pinned === true,
    position: Number.isFinite(Number(data?.position)) ? Number(data.position) : 0,
    dateCreated: data?.dateCreated || null,
    dateModified: data?.dateModified || null,
    sourcePath: normalizeHelpString(options.sourcePath || data?.sourcePath),
  };
}

/**
 * Builds an editor draft for a new or existing article.
 *
 * @param {object|null} [article]
 * @returns {object}
 */
export function buildHelpArticleDraft(article = null) {
  if (article) return normalizeHelpArticle(article, article.key, {
    sourcePath: article.sourcePath,
  });

  return normalizeHelpArticle({
    key: generateKey(),
    typeKeys: [],
    title: '',
    tags: [],
    content: createRichTextValue(''),
    contentText: '',
    published: false,
    pinned: false,
    position: 0,
  });
}

/**
 * Maps a query snapshot to normalized articles.
 *
 * @param {object} snapshot
 * @returns {object[]}
 */
function articlesFromSnapshot(snapshot) {
  return snapshot.docs
    .map((docSnap) => normalizeHelpArticle(
      docSnap.data() || {},
      docSnap.id,
      { sourcePath: docSnap.ref.path },
    ))
    .sort(compareHelpArticles);
}

/**
 * Subscribe to every help article for admin.
 *
 * @param {function(object[]): void} callback
 * @param {object} [options]
 * @param {boolean} [options.publishedOnly]
 * @returns {function(): void}
 */
export function subscribeToAllHelpArticles(callback, options = {}) {
  if (typeof callback !== 'function') return () => {};

  const publishedOnly = options.publishedOnly === true;
  const articleQuery = publishedOnly
    ? query(collectionGroup(db, ARTICLES_SUBCOLLECTION), where('published', '==', true))
    : collectionGroup(db, ARTICLES_SUBCOLLECTION);

  return onSnapshot(
    articleQuery,
    (snapshot) => {
      callback(articlesFromSnapshot(snapshot));
    },
    (error) => {
      console.error('[HelpArticleService] subscribeToAllHelpArticles error:', error);
      callback([]);
    },
  );
}

/**
 * Subscribe to every published help article for in-app search.
 *
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToPublishedHelpArticles(callback) {
  return subscribeToAllHelpArticles(callback, { publishedOnly: true });
}

/**
 * One-shot list used by tests and admin fallbacks.
 *
 * @returns {Promise<object[]>}
 */
export async function listHelpArticles() {
  const snapshot = await getDocs(helpLibraryCollectionRef());
  return articlesFromSnapshot(snapshot);
}

/**
 * Persist a help article into the shared library.
 *
 * @param {object} draft
 * @param {object[]} [existingArticles]
 * @returns {Promise<object>}
 */
export async function saveHelpArticle(draft, existingArticles = []) {
  const articleId = normalizeHelpString(draft?.key) || generateKey();
  const title = normalizeHelpString(draft?.title);
  if (!title) throw new Error('Title is required.');

  const typeKeys = parseHelpArticleTypeKeys(draft?.typeKeys, draft?.typeKey);
  const content = normalizeRichTextValue(draft?.content);
  const contentText = extractRichTextPlainText(content);
  const existing = (Array.isArray(existingArticles) ? existingArticles : [])
    .find((row) => normalizeHelpString(row?.key) === articleId);
  const now = Timestamp.now();
  const position = Number.isFinite(Number(existing?.position)) && Number(existing.position) > 0
    ? Number(existing.position)
    : (Number.isFinite(Number(draft?.position)) && Number(draft.position) > 0
      ? Number(draft.position)
      : getNextHelpArticlePosition(existingArticles));

  const payload = {
    key: articleId,
    typeKey: typeKeys[0] || HELP_APP_TYPE_KEY,
    typeKeys,
    title,
    tags: parseHelpArticleTags(draft?.tags),
    content,
    contentText,
    published: draft?.published === true,
    pinned: draft?.pinned === true,
    position,
    dateCreated: existing?.dateCreated || draft?.dateCreated || now,
    dateModified: now,
  };

  await setDoc(helpLibraryDocRef(articleId), payload, { merge: true });

  const sourcePath = normalizeHelpString(draft?.sourcePath || existing?.sourcePath);
  if (sourcePath && !isLibraryPath(sourcePath)) {
    const leftoverRef = docRefFromPath(sourcePath);
    if (leftoverRef) {
      await deleteDoc(leftoverRef);
    }
  }

  return normalizeHelpArticle(payload, articleId, {
    sourcePath: `${LIBRARY_PREFIX}${articleId}`,
  });
}

/**
 * Deletes a help article document.
 *
 * @param {object|string} articleOrId
 * @returns {Promise<void>}
 */
export async function deleteHelpArticle(articleOrId) {
  const articleId = normalizeHelpString(
    typeof articleOrId === 'string' ? articleOrId : articleOrId?.key,
  );
  if (!articleId) return;

  const sourcePath = typeof articleOrId === 'string'
    ? ''
    : normalizeHelpString(articleOrId?.sourcePath);
  const targetRef = sourcePath ? docRefFromPath(sourcePath) : helpLibraryDocRef(articleId);
  if (targetRef) {
    await deleteDoc(targetRef);
  }

  if (sourcePath && !isLibraryPath(sourcePath)) {
    await deleteDoc(helpLibraryDocRef(articleId)).catch(() => {});
  }
}

/**
 * Uploads a screenshot for a help article and returns image attrs.
 *
 * @param {string} articleId
 * @param {Blob} file
 * @param {string[]} [typeKeys]
 * @returns {Promise<{ storagePath: string, src: string, alt: string }>}
 */
export async function uploadHelpArticleImage(articleId, file, typeKeys = []) {
  const cleanedArticleId = normalizeHelpString(articleId);
  if (!cleanedArticleId) {
    throw new Error('articleId is required');
  }
  if (!(file instanceof Blob)) {
    throw new Error('An image file is required');
  }
  if (file.size > HELP_IMAGE_MAX_BYTES) {
    throw new Error('Images must be 8 MB or smaller.');
  }

  const mime = String(file.type || '').trim().toLowerCase();
  if (mime && !mime.startsWith('image/')) {
    throw new Error('Only image files can be added to articles.');
  }

  const ext = extensionForImage(file);
  const scope = storageScopeForTypeKeys(getHelpArticleTypeKeys({ typeKeys }));
  const storagePath = `help/${scope}/${cleanedArticleId}/${generateKey(12)}.${ext}`;
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  const metadata = mime ? { contentType: mime } : undefined;
  await uploadBytes(storageRef, file, metadata);

  return {
    alt: normalizeHelpString(file.name).replace(/\.[^.]+$/, ''),
    src: '',
    storagePath,
  };
}

/**
 * Picks a file extension for an uploaded help image.
 *
 * @param {Blob} file
 * @returns {string}
 */
function extensionForImage(file) {
  const name = String(file?.name || '').trim();
  const fromName = name.includes('.') ? name.split('.').pop() : '';
  if (fromName) return fromName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';

  const mime = String(file?.type || '').trim().toLowerCase();
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  return 'jpg';
}
